import React, { useState, useEffect } from "react";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { Checkbox, Divider } from "@mui/material";
import { User } from "lucide-react";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  paymentData: {
    discount: string;
    ReceivedAmount: string;
    paymentMethod: string;
  };
  onPaymentDataChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessPayment: () => void;
  isProcessing: boolean;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, order, paymentData, onPaymentDataChange, onProcessPayment, isProcessing }) => {
  const [localReceivedAmount, setLocalReceivedAmount] = useState("0");
  const [error, setError] = useState("");
  const [includeOutstanding, setIncludeOutstanding] = useState(false);

  useEffect(() => {
    setLocalReceivedAmount(paymentData.ReceivedAmount || "0");
  }, [paymentData.ReceivedAmount]);

  const methodLower = (paymentData.paymentMethod || "").toLowerCase().trim();
  const isBuyOnCredit = methodLower === "buy on credit" || methodLower === "paylater" || methodLower === "pay late";
  const isCashLike = !isBuyOnCredit;

  // Get the current order (first order)
  const currentOrder = order?.orders?.[0];

  // Calculate totals from all orders if multiple exist
  const totalOrderAmount = order?.orders?.reduce((sum, o) => sum + Number(o.orderAmount || 0), 0) || 0;
  const totalDiscount = order?.orders?.reduce((sum, o) => sum + Number(o.discount || 0), 0) || 0;

  // Calculate total received from payment history (all payments)
  const totalReceivedFromHistory = order?.paymentHistory?.reduce((sum, p) => sum + Number(p.receivedAmount || 0), 0) || 0;

  // Get remaining amount from the order
  const orderRemainingAmount = Number(currentOrder?.remainingAmount || 0);
  const oldRemaining = orderRemainingAmount;

  // Get outstanding balance from order
  const customerOutstanding = Number(order?.totalOutstandingBalance || 0) - (Number(order?.orders[0]?.remainingAmount) || 0);

  // Calculate total remaining including outstanding if checkbox is checked
  const totalRemaining = oldRemaining + (includeOutstanding ? customerOutstanding : 0);

  const enteredAmount = parseFloat(localReceivedAmount || "0");

  // Validate amount against totalRemaining
  const isValidAmount = enteredAmount >= 0 && enteredAmount <= totalRemaining && !isNaN(enteredAmount);

  // Calculate new remaining based on totalRemaining
  const newRemaining = Math.max(totalRemaining - enteredAmount, 0);

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ") || "N/A";

  const handleFocus = () => {
    if (localReceivedAmount === "0") setLocalReceivedAmount("");
    setError("");
  };

  const handleBlur = () => {
    if (localReceivedAmount.trim() === "") {
      setLocalReceivedAmount("0");
      const syntheticEvent = {
        target: { name: "ReceivedAmount", value: "0" },
      } as React.ChangeEvent<HTMLInputElement>;
      onPaymentDataChange(syntheticEvent);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Update local state first
    setLocalReceivedAmount(value);

    // Always update parent state
    onPaymentDataChange(e);

    // Then validate and show error
    if (value === "") {
      setError("");
      return;
    }

    const enteredVal = parseFloat(value);
    if (isNaN(enteredVal)) {
      setError("Please enter a valid number");
      return;
    }

    if (enteredVal > totalRemaining) {
      setError(`Amount cannot exceed ${totalRemaining.toFixed(2)} QAR`);
    } else {
      setError("");
    }
  };

  const roundAmount = (value: any) => {
    return Number(value).toFixed(2);
  };

  if (!open) return null;

  return (
    <ModalWrapper title="Payment Summary" onClose={onClose} onBack={onClose} showCloseButton showBackButton width="max-w-md">
      <div className="text-left -pb-5">
        {/* Customer Info - matches PaymentSummaryContent exactly */}
        <div className="flex items-center gap-3 bg-gray-100 p-3 px-5 mb-2 rounded-md">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
            <User className="text-gray-600" size={22} />
          </div>
          <div className="flex flex-col text-sm">
            <span className="font-semibold">Customer: {customerName}</span>
            <span className="text-gray-600">Payment: {paymentData.paymentMethod}</span>
            <span className="text-gray-500 text-xs">Order ID: {currentOrder?.orderId || "N/A"}</span>
          </div>
        </div>

        {/* Order Summary - matches PaymentSummaryContent exactly */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700 mb-1">Order Summary</h3>
          <Divider className="mb-1.5" />

          {/* Outstanding Balance - matches PaymentSummaryContent exactly */}
          {!isBuyOnCredit && (
            <div className="flex items-center justify-between text-sm">
              <div>
                <Checkbox onChange={() => setIncludeOutstanding(!includeOutstanding)} checked={includeOutstanding} size="small" disabled={customerOutstanding <= 0} />
                <span className="font-medium">Outstanding Balance</span>
              </div>
              <span className="font-medium">{roundAmount(customerOutstanding)} QAR</span>
            </div>
          )}

          <div className="space-y-2 text-sm">
            {/* Outstanding Balance for Buy on Credit - matches PaymentSummaryContent exactly */}
            {isBuyOnCredit && (
              <div className="flex mt-2 justify-between">
                <span className="px-10">Outstanding Balance</span>
                <span className="font-medium">{roundAmount(customerOutstanding)} QAR</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="px-10">Order Amount</span>
              <span className="font-medium">{roundAmount(totalOrderAmount)} QAR</span>
            </div>
            <div className="flex justify-between">
              <span className="px-10">Discount</span>
              <span>- {roundAmount(totalDiscount)} QAR</span>
            </div>
            <div className="flex justify-between">
              <span className="px-10">Total Received</span>
              <span className="font-medium">{roundAmount(totalReceivedFromHistory)} QAR</span>
            </div>
            <div className="flex justify-between">
              <span className="px-10">Remaining Amount</span>
              <span className="font-medium">{roundAmount(totalRemaining)} QAR</span>
            </div>
          </div>
        </div>

        {/* Payment Status - matches PaymentSummaryContent exactly */}
        <div className="mb-2">
          <h3 className="font-semibold text-gray-700 mb-1">Payment Status</h3>
          <Divider className="mb-2" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="px-10 mt-2">Total Payable</span>
              <span className="font-medium mt-2">{roundAmount(totalRemaining)} QAR</span>
            </div>

            {/* Received Amount - matches PaymentSummaryContent exactly */}
            {isBuyOnCredit ? (
              <div className="flex justify-between">
                <span className="px-10">Received Amount</span>
                <span className="font-medium">0 QAR</span>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="px-10">Received Amount</span>
                <div className="flex flex-col items-end">
                  <input
                    name="ReceivedAmount"
                    type="text"
                    value={localReceivedAmount}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === "+") e.preventDefault();
                    }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData("Text");
                      if (parseFloat(pasted) < 0) e.preventDefault();
                    }}
                    placeholder="0"
                    disabled={isProcessing || totalRemaining <= 0}
                    className={`w-28 h-9 rounded-md border px-2.5 text-right text-sm text-gray-900 focus:outline-none ${
                      error ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-blue-400"
                    }`}
                  />
                  {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
                </div>
              </div>
            )}

            {/* Remaining Amount - matches PaymentSummaryContent exactly */}
            <div className="flex justify-between">
              <span className="px-10">New Remaining Amount</span>
              <span className="font-medium">{isCashLike ? roundAmount(newRemaining) : roundAmount(totalRemaining - parseFloat(paymentData.ReceivedAmount || "0"))} QAR</span>
            </div>
          </div>
        </div>

        {/* Footer with Actions */}
        <Divider className="my-2" />
        <div className="flex justify-end p-2 gap-3">
          <CommonButtons className="h-10 w-32" label="Cancel" onClick={onClose} variant="outlineDanger" disabled={isProcessing} />
          <CommonButtons
            className="h-10 w-36"
            label={isProcessing ? "Processing..." : "Complete"}
            onClick={onProcessPayment}
            variant="primary"
            disabled={isProcessing || (!isBuyOnCredit && (!isValidAmount || localReceivedAmount.trim() === "" || parseFloat(localReceivedAmount) <= 0))}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default PaymentDialog;
